import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { Options as ReactMarkdownOptions } from 'react-markdown';
import Link from '../../navigation/link/Link';

type MarkdownProps = Omit<ReactMarkdownOptions, 'children' | 'components'> & {
    children?: React.ReactNode;
    removeWrapper?: boolean;
    components?: ReactMarkdownOptions['components'];
};

const defaultComponents: ReactMarkdownOptions['components'] = {
    a: ({ node, children, ...props }) => {
        const href = (props as any).href ?? '';
        const isExternal = /^https?:\/\//i.test(href);
        const label =
            (node?.children?.[0] as any)?.value ??
            (Array.isArray(children)
                ? children.map(c => (typeof c === 'string' || typeof c === 'number' ? String(c) : '')).join('')
                : typeof children === 'string' || typeof children === 'number'
                  ? String(children)
                  : '');

        return isExternal ? (
            <Link href={href} target="_blank" rel="noopener noreferrer">
                {label}
            </Link>
        ) : (
            <Link to={href}>{label}</Link>
        );
    },
};

const coerceToString = (node: React.ReactNode): string => {
    if (node == null) return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(coerceToString).join('');
    return '';
};

const Markdown: React.FC<MarkdownProps> = ({ children, components, removeWrapper, ...otherProps }) => {
    const merged: ReactMarkdownOptions['components'] = { ...defaultComponents, ...(components || {}) };

    if (removeWrapper) {
        merged.p = ({ children }) => <>{children}</>;
    }

    const text = coerceToString(children);

    return (
        <ReactMarkdown components={merged} {...otherProps}>
            {text}
        </ReactMarkdown>
    );
};

Markdown.displayName = 'Markdown';
export default Markdown;
